import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Table,
    DatePicker,
    Row,
    Col,
    Button,
    Input,
    Icon,
    Menu,
    Dropdown,
    Modal,
    Form,
    Empty,
    Typography
} from "antd";
import { CdrService } from "../../services/cdr/CdrService";
import { handleError } from "../../utils/ErrorHandlers";
import Page from "../general/Page";
import CDRSwitchedCalls from "./CDRSwitchedCalls";
import Highlighter from "react-highlight-words";
import "./CDRTable.css";
import AudioPlayer from "../streaming/AudioPlayer";
import moment from "moment";
import { convertTZToLocalCDR } from "../general/DateTzHelper";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Translation } from "react-i18next";
import i18next from "i18next";
import ar from "antd/es/date-picker/locale/ar_EG";
import en from "antd/es/date-picker/locale/en_US";
import SharedStateContext from "../../contexts/shared-state/SharedStateContext";
import useCdrExport from "./useCdrExport";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { Constants } from "../../data/constants";
import { ca } from "date-fns/locale";
import { set } from "lodash";

const { RangePicker } = DatePicker;
const { Text } = Typography;

export default function CDRTableComponent() {

    const searchInputRef = useRef(null);
    const reloadRef = useRef(false);
    const context = useContext(SharedStateContext);
    const cdrService = useMemo(() => new CdrService(), []);
    const enableCallRecordExpiration = new Constants().enableCallRecordExpiration;
    const enableVoiceMailExpiration = new Constants().enableVoiceMailExpiration;

    const [searchText, setSearchText] = useState({});

    const [isLoading, setIsLoading] = useState({
        isFetching: false,
        isExporting: false
    });
    const [data, setData] = useState([]);

    const [date, setDate] = useState(
        {
            from: moment().locale("en").startOf("day").format("YYYY-MM-DD HH:mm:ss"),
            to: moment().locale("en").endOf("day").format("YYYY-MM-DD HH:mm:ss")
        }
    )

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const [selection, setSelection] = useState(
        {
            isSelected: false,
            selectedData: [],
            idsList: [],
            selectedCallRecordsId: [],
            selectedVoiceMailsId: [],
            selectedRowKeys: [],
            selectedCallRecordsDownloadable: [],
            selectedVoiceMailsDownloadable: []
        }
    )


    const [filters, setFilters] = useState({});
    const location = useLocation();

    const headers = {
        cdrHeaders: [
            "Cdr Id", "A Number", "Customer Name", "B Number", "Call Record",
            "Voicemail", "Start Time", "End Time", "Switched Time",
            "Call Result", "Call Duration", "Call Direction"
        ],
        cdrSwitchedHeaders: [
            "Cdr Id", "Username", "Switch Time", "Switching Status",
            "Call Status", "Answer Time", "End Time"
        ]
    };

    const [showModal, setShowModal] = useState(false);
    const [fileName, setFileName] = useState("");
    const [operationKey, setOperationKey] = useState(0);
    const [lang] = useState(
        context.curr_lang === "ar" && context.curr_dir === "rtl"
            ? "ar"
            : "en"
    );
    const [pagination, setPagination] = useState({});
    const [selectionMaps, setSelectionMaps] = useState({
        selectedMap: new Map(),
        selectedIdsMap: new Map()
    });
    const [redefinedData, formatDataToExport,
        formatSwitchDataToExport, createCsvFile,
        createCsvFileDataNoSwitched, createSelectedCdrZipFolder,
        createAllCdrZipFolder] = useCdrExport();

    const fromRef = useRef();
    const toRef = useRef();
    const searchRef = useRef([]);



    useEffect(() => {
        resetAllStates();
        context.setCurrentComponent("cdrs");
        getAllCdrs();
    }, [location.key]);


    useEffect(() => {
        fromRef.current = date.from;
        toRef.current = date.to;
    }, [date]);


    const getAllCdrs = async (params = {}) => {
        try {

            const currentFrom = params?.from === undefined ? date.from : params.from;
            const currentTo = params?.to === undefined ? date.to : params.to;

            setIsLoading({ ...isLoading, isFetching: true });
            let data = await cdrService.getCdrsFromToStartDatePeagable(
                context.session_id,
                currentFrom,
                currentTo,
                params?.page,
                params?.size,
                params?.filters
            );

            setPagination(prev => ({
                ...prev,
                total: data.totalRecords,
                current: params?.page === undefined ? 1 : params.page
            }));
            const formatedData = formatData(data);
            setData(formatedData);
            setIsLoading({ ...isLoading, isFetching: false });
        } catch (e) {
            setIsLoading({ ...isLoading, isFetching: false });
            handleError(e);
        }


    }

    function formatData(data) {
        const updated_data = [];
        if (data.ivrList === null) {
            return [];
        }
        for (var i = 0; i < data.ivrList.length; i++) {
            var row = {};
            row["key"] = i + 1;
            row["cdrId"] = data.ivrList[i]["cdrId"];
            row["a"] = data.ivrList[i]["a"];
            row["b"] = data.ivrList[i]["b"];
            row["startTime"] = convertTZToLocalCDR(data.ivrList[i]["startTime"]);
            row["endTime"] = convertTZToLocalCDR(data.ivrList[i]["endTime"]);
            row["switchTime"] = data.ivrList[i]["isSwitched"]
                ? convertTZToLocalCDR(data.ivrList[i]["switchTime"])
                : "---";
            row["isSwitched"] = data.ivrList[i]["isSwitched"].toString();
            row["callResult"] = data.ivrList[i]["callResult"];
            row["customerName"] = data.ivrList[i]["fullname"]
                ? data.ivrList[i]["fullname"]
                : "---";
            row["callDuration"] = new Date(
                parseFloat(data.ivrList[i]["callDuration"]).toFixed(2) * 1000
            )
                .toISOString()
                .substr(11, 8);

            row["callDirection"] = data.ivrList[i]["callDirection"];
            row["hasVoiceMail"] = data.ivrList[i]["hasVoiceMail"];
            row["isExpired"] = isExpiredWithStartOfDay(convertTZToLocalCDR(data.ivrList[i]["startTime"]));
            updated_data.push(row);
        }
        return updated_data;

    }

    async function getAllSwitchedCRD(from, to) {
        try {
            setIsLoading({ ...isLoading, isExporting: true });

            let data = await cdrService.getAllSwitchedCRDDateService(
                context.session_id,
                from,
                to
            );
            setIsLoading({ ...isLoading, isExporting: false });
            return data.ivrList;
        } catch (e) {
            handleError(e);
        }
    }

    async function getSelectedSwitchedCRD(ids) {
        try {
            setIsLoading({ ...isLoading, isExporting: true });
            let data = await cdrService.getSelectedSwitchedCRDDateService(
                context.session_id,
                ids
            );

            setIsLoading({ ...isLoading, isExporting: false });
            return data.ivrList;
        } catch (e) {
            handleError(e);
        }
    }

    const handleTableChange = async (pagination, sorter) => {
        if (reloadRef.current) {
            reloadRef.current = false;
            return;
        }
        await getAllCdrs(
            {
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                from: date.from,
                to: date.to,
                filters: filters
            }
        );
    };

    async function getAllRecordsFromDateToDate() {
        try {
            setIsLoading({ ...isLoading, isFetching: true });
            let data = await cdrService.getCdrsFromToStartDate(
                context.session_id,
                date.from,
                date.to
            );
            // setPagination({ ...pagination, total: data.totalRecords });
            const fomratedData = formatData(data);
            setIsLoading({ ...isLoading, isFetching: false });
            return fomratedData;
        } catch (e) {
            setIsLoading({ ...isLoading, isFetching: false });
            handleError(e);
        }

    }

    async function onOk(dates) {
        try {
            resetAllStates();
            const newFrom = dates[0].format("YYYY-MM-DD HH:mm:ss").toString();
            const newTo = dates[1].format("YYYY-MM-DD HH:mm:ss").toString();
            if (date.from !== dates[0].format("YYYY-MM-DD HH:mm:ss").toString() || date.to !== dates[1].format("YYYY-MM-DD HH:mm:ss").toString()) {
                setPagination({ ...pagination, current: 1 });
            }
            setDate({ ...date, from: newFrom, to: newTo });

            await getAllCdrs({
                page: 1,
                from: newFrom,
                to: newTo
            });

            const fomratedData = {
                newFrom: newFrom,
                newTo: newTo
            }


            return fomratedData;
        } catch (e) {
            setIsLoading({ ...isLoading, isFetching: false });
            handleError(e);
            return null
        }

    }

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
        }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInputRef}
                    placeholder={i18next.t(`Search ${dataIndex}`)}
                    value={selectedKeys?.[0] || ""}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() => {
                        handleSearch(selectedKeys, confirm, dataIndex);
                    }}
                    style={{ width: 188, marginBottom: 8, display: "block" }}
                />
                <Button
                    type="primary"
                    onClick={() => {
                        handleSearch(selectedKeys, confirm, dataIndex);
                    }}
                    icon="search"
                    size="small"
                    style={
                        context.curr_dir === "ltr" &&
                            context.curr_lang === "en"
                            ? { width: 100, marginRight: 8 }
                            : { width: 100, marginLeft: 8 }
                    }
                >
                    {i18next.t("Search")}
                </Button>
                <Button
                    onClick={() => {
                        clearFilters();
                        const updatedFilters = { ...filters };
                        delete updatedFilters[dataIndex];
                        setFilters(updatedFilters);
                        let currentSearchText = { ...searchText };
                        delete currentSearchText[dataIndex];
                        setSearchText(currentSearchText);
                        searchRef.current = [];
                        reloadRef.current = true;
                        getAllCdrs(
                            {
                                from: fromRef.current,
                                to: toRef.current,
                                filters: updatedFilters
                            }
                        );
                    }}
                    size="small"
                    style={{ width: 80 }}
                >
                    {i18next.t("Reset")}
                </Button>
            </div>
        ),
        filterIcon: (filtered) => (
            <Icon type="search" style={{ color: "#d73a31" }} />
        ),
        onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInputRef.current?.select());
            }
        },
    });

    const handleSearch = async (selectedKeys, confirm, dataIndex) => {
        confirm();
        let newFilters;
        if (selectedKeys[0] === undefined || selectedKeys[0].trim() === "") {
            selectedKeys[0] = undefined;
             newFilters = {
                ...filters
            };
            delete newFilters[dataIndex];
        } else {
             newFilters = {
                ...filters,
                [dataIndex]: selectedKeys[0],
            };
        }
        reloadRef.current = true;

        searchRef.current = [...searchRef.current, selectedKeys[0]];
        setSearchText(
            {
                ...searchText,
                [dataIndex]: selectedKeys[0]
            }
        );
        setFilters(newFilters);
        await getAllCdrs({
            page: 1,
            filters: newFilters,
            from: fromRef.current,
            to: toRef.current
        });

    };

    const handleOk = async () => {
        setShowModal(false);
        let dataToUse;

        if (selection.isSelected) {
            dataToUse = redefinedData(selection.selectedData, headers.cdrHeaders);

        } else {
            const allData = await getAllRecordsFromDateToDate(pagination.total);
            const formatted = formatDataToExport(allData);
            dataToUse = redefinedData(formatted, headers.cdrHeaders);
        }

        handleMenuClick(
            operationKey,
            dataToUse,
            fileName
        );
    }

    const handleCancel = () => {
        setShowModal(false);
    };

    function customExpandIcon(props) {
        const { expanded, onExpand, record } = props;
        if (expanded) {
            return (
                <Button
                    icon={expanded ? "minus" : "plus"}
                    size="small"
                    className="custom_expand_button"
                    onClick={(e) => onExpand(record, e)}
                    disabled={record.isSwitched === "true" ? false : true}
                />
            );
        } else {
            return (
                <Button
                    icon="plus"
                    size="small"
                    className="custom_expand_button"
                    onClick={(e) => {
                        props.onExpand(props.record, e);
                    }}
                    disabled={record.isSwitched == "true" ? false : true}
                />
            );
        }
    }

    const handleMenuClick = async (e, data, fileName) => {
        if (e == 1 && selection.isSelected == false) {
            let switchedData = await getAllSwitchedCRD(
                date.from,
                date.to
            );

            let redefinedSwitchedData = redefinedData(
                formatSwitchDataToExport(switchedData),
                headers.cdrSwitchedHeaders
            );

            const zip = new JSZip();
            zip.file("CDR.csv", createCsvFile(data));

            zip.file("Switched.csv", createCsvFile(redefinedSwitchedData));
            zip.generateAsync({ type: "blob" }).then(function (content) {
                saveAs(content, fileName + ".zip");
            });
        } else if (e == 1 && selection.isSelected == true) {
            const zip = new JSZip();
            if (selection.idsList.length != 0) {
                let switchedData = await getSelectedSwitchedCRD(
                    selection.idsList
                );

                let redefinedSwitchedData = redefinedData(
                    formatSwitchDataToExport(switchedData),
                    headers.cdrSwitchedHeaders
                );

                zip.file("Switched.csv", createCsvFile(redefinedSwitchedData));
                zip.file("CDR.csv", createCsvFile(data));

                zip.generateAsync({ type: "blob" }).then(function (content) {
                    saveAs(content, fileName + ".zip");
                });
            } else {
                createCsvFileDataNoSwitched(data, fileName);
            }
        } else if (e == 2 && selection.isSelected == true) {
            setIsLoading({ ...isLoading, isExporting: true });
            await createSelectedCdrZipFolder(data, fileName + ".zip", selection.idsList, headers.cdrSwitchedHeaders, selection.selectedCallRecordsDownloadable, selection.selectedVoiceMailsDownloadable);
            setIsLoading({ ...isLoading, isExporting: false });
        } else if (e == 2 && selection.isSelected == false) {
            setIsLoading({ ...isLoading, isExporting: true });
            await createAllCdrZipFolder(data, fileName + ".zip", date.from, date.to, headers.cdrSwitchedHeaders, isExpiredWithStartOfDay, enableCallRecordExpiration, enableVoiceMailExpiration);
            setIsLoading({ ...isLoading, isExporting: false });
        }
    };

    const showModalContent = (e) => {
        setOperationKey(e.key);
        setShowModal(true);
    };

    const getExportFileName = (e) => {
        setFileName(e.target.value);
        e.target.value = "";
    };

    const populateIdsList = (selectedMap) => {
        const idsList = [];
        const callRecordIds = [];
        const voiceMailsIds = [];
        const callRecordsDownloadable = [];
        const voiceMailsDownloadable = [];
        for (const cdrArr of selectedMap.values()) {
            for (const cdr of cdrArr) {
                if (cdr.isSwitched == "true") idsList.push(cdr.cdrId);
                if (cdr.isSwitched == "true" && cdr.callResult === "ANSWERED_BY_AGENT") {
                    if (enableCallRecordExpiration) {
                        if (!(cdr.isExpired)) callRecordsDownloadable.push(cdr.cdrId);
                    } else {
                        callRecordsDownloadable.push(cdr.cdrId);
                    }
                }
                if (cdr.hasVoiceMail) {
                    if (enableVoiceMailExpiration) {
                        if (!(cdr.isExpired)) voiceMailsDownloadable.push(cdr.cdrId);
                    } else {
                        voiceMailsDownloadable.push(cdr.cdrId);
                    }
                }
            }
        }
        return [idsList, callRecordIds, voiceMailsIds, callRecordsDownloadable, voiceMailsDownloadable];

    }

    function isExpiredWithStartOfDay(startTime) {
        let expirationDate = moment().subtract(5, 'days');
        let startTimeMoment = moment(startTime, 'DD-MM-YYYY HH:mm:ss', true).startOf("day");
        expirationDate = expirationDate.startOf("day");
        return startTimeMoment.isSameOrBefore(expirationDate);
    }

    function isExpiredWithExactTime(startTime) {
        let expirationDate = moment().subtract(3, 'days');
        let startTimeMoment = moment(startTime, 'DD-MM-YYYY HH:mm:ss', true);
        return startTimeMoment.isSameOrBefore(expirationDate);
    }




    const getArraysOfMap = (selectedMap) => {
        const result = [];
        for (let arr of selectedMap.values()) {
            for (let element of arr) {
                result.push(element);
            }
        }
        return result;
    }

    const columns = useMemo(() => [
        {
            title: i18next.t("A number"),
            dataIndex: "a",
            key: "a",
            ...getColumnSearchProps("aNumber"),
            render: (text) =>
                searchText['aNumber'] ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                        searchWords={[searchText['aNumber']]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ""}
                    />
                ) : (
                    text
                ),
        },
        {
            title: i18next.t("Customer name"),
            dataIndex: "customerName",
            key: "customerName",
            ...getColumnSearchProps("customerName", "Customer Name"),
            render: (text) =>
                searchText["customerName"] ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                        searchWords={[searchText["customerName"]]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ""}
                    />
                ) : (
                    text
                ),
        },
        {
            title: i18next.t("B number"),
            dataIndex: "b",
            key: "b",
            ...getColumnSearchProps("bNumber"),
            render: (text) =>
                searchText["bNumber"] ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                        searchWords={[searchText['bNumber']]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ""}
                    />
                ) : (
                    text
                ),
        },
        {
            title: i18next.t("Call Record"),
            dataIndex: "callRecord",
            key: "callRecord",
            render: (text, record) =>
                record.isSwitched === "true" &&
                    record.callResult === "ANSWERED_BY_AGENT" ? (
                    record.isExpired && enableCallRecordExpiration // -> flag to enable or disbale the feature
                        ? <AudioPlayer record={record} type="record" disabled={true} />
                        : <AudioPlayer record={record} type="record" />
                ) : (
                    "---"
                ),
        },
        {
            title: i18next.t("Voicemail"),
            dataIndex: "voicemail",
            key: "voicemail",
            render: (text, record) =>
                record.hasVoiceMail ? (
                    record.isExpired && enableVoiceMailExpiration
                        ? <AudioPlayer record={record} type="voiceMail" disabled={true} />
                        : <AudioPlayer record={record} type="voiceMail" />
                ) : (
                    "---"
                ),
            // record.callResult === "ANSWERED_VOICEMAIL" ? (
            //   <AudioPlayer record={record} type="voiceMail" />
            // ) : (
            //   "---"
            // ),
        },
        {
            title: i18next.t("Start time"),
            dataIndex: "startTime",
            key: "startTime",
        },
        {
            title: i18next.t("End time"),
            dataIndex: "endTime",
            key: "endTime",
        },
        {
            title: i18next.t("Switched Time"),
            dataIndex: "switchTime",
            key: "switchTime",
        },
        {
            title: i18next.t("Call result"),
            dataIndex: "callResult",
            key: "callResult",
            ...getColumnSearchProps("callResult"),
            render: (text) =>
                searchText['callResult'] ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                        searchWords={[searchText['callResult']]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ""}
                    />
                ) : (
                    text
                ),
        },
        {
            title: i18next.t("Call duration"),
            dataIndex: "callDuration",
            key: "callDuration",
        },
        {
            title: i18next.t("Call direction"),
            dataIndex: "callDirection",
            key: "callDirection",
            ...getColumnSearchProps("callDirection"),
            render: (text) =>
                searchText["callDirection"] ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                        searchWords={[searchText["callDirection"]]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ""}
                    />
                ) : (
                    text
                ),
        },
    ], [searchText]);

    const menu = (
        <Menu onClick={(e) => showModalContent(e)}>
            <Menu.Item key="1"> {i18next.t("Data only")} </Menu.Item>
            <Menu.Item key="2"> {i18next.t("Data with files")} </Menu.Item>
        </Menu>
    );

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRowKeys(selectedRowKeys);
            const currentPage = pagination.current;
            const data = formatDataToExport(selectedRows, columns);
            const selectedMap = selectionMaps.selectedMap;
            const selectedIdsMap = selectionMaps.selectedIdsMap;
            if (data.length === 0) {
                selectedMap.delete(currentPage);
                selectedIdsMap.delete(currentPage);
            } else {
                selectedMap.set(currentPage, data);
                selectedIdsMap.set(currentPage, selectedRows);
            }
            setSelectionMaps({
                selectedMap: selectedMap,
                selectedIdsMap: selectedIdsMap
            });

        }
    };


    useEffect(() => {
        let flag = false;
        if (selectionMaps.selectedMap.size !== 0 || selectionMaps.selectedIdsMap.size !== 0) {
            flag = true;
        } else {
            flag = false;
        }

        const [idsList, callRecordIds, voiceMailsIds, callRecordsDownloadable, voiceMailsDownloadable] = populateIdsList(selectionMaps.selectedIdsMap);
        const data = getArraysOfMap(selectionMaps.selectedMap);

        setSelection({
            ...selection,
            isSelected: flag,
            selectedData: data,
            idsList: idsList,
            selectedCallRecordsId: callRecordIds,
            selectedVoiceMailsId: voiceMailsIds,
            selectedCallRecordsDownloadable: callRecordsDownloadable,
            selectedVoiceMailsDownloadable: voiceMailsDownloadable
        });

    }, [selectionMaps]);

    const showCdrSwitchedCall = (record) => {
        return (
            <CDRSwitchedCalls
                record={record}
                from={date.from}
                to={date.to}
            />

        );
    }

    const resetAllStates = () => {
        setFilters({});
        setSearchText([]);
        setSelectionMaps({
            selectedMap: new Map(),
            selectedIdsMap: new Map()
        });
        setSelection(
            {
                isSelected: false,
                selectedData: [],
                idsList: [],
                selectedCallRecordsId: [],
                selectedVoiceMailsId: [],
                selectedRowKeys: [],
                selectedCallRecordsDownloadable: [],
                selectedVoiceMailsDownloadable: []
            }
        );
        setData([])
        searchRef.current = [];
        setSelectedRowKeys([]);
    }



    return (
        <Translation>
            {(t) => (
                <Page title={t("CDR Table")}>
                    <Row
                        style={{
                            marginTop: "30px",
                            marginBottom: "8px",
                            alignItems: "center",
                            fontSize: "18px",
                        }}
                    >
                        <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                            <RangePicker
                                showTime={true}
                                format={"YYYY-MM-DD HH:mm:ss"}
                                onChange={(e) => {
                                    if (e.length <= 0) {
                                        resetAllStates();
                                        getAllCdrs({
                                            page: pagination.current,
                                            results: pagination.pageSize,
                                            from: moment().locale("en").startOf("day").format("YYYY-MM-DD HH:mm:ss"),
                                            to: moment().locale("en").endOf("day").format("YYYY-MM-DD HH:mm:ss"),
                                            filters: {}
                                        });
                                    }
                                }}
                                onOk={async (e) => {
                                    await onOk(e);
                                }}
                                placeholder={[i18next.t("from"), i18next.t("to")]}
                                defaultValue={[
                                    moment().startOf("day"),
                                    moment().endOf("day"),
                                ]}
                                locale={lang === "ar" ? ar : en}
                            />

                            <Text style={{ margin: 5, color: "#d73a31", fontSize: 16 }}>
                                {t("Please note that call records expire after 5 days.")}
                            </Text>
                        </Col>
                        <Dropdown
                            overlay={menu}
                            disabled={data.length != 0 ? false : true}
                        >
                            <Button
                                className="button-submit"
                                type="primary"
                                size="default"
                                loading={isLoading.isExporting}
                            >
                                {selection.isSelected
                                    ? t("Export Selected")
                                    : t("Export All")}{" "}
                                <Icon type="download" />
                            </Button>
                        </Dropdown>
                    </Row>
                    <div>

                        <Table
                            className="components-table-demo-nested"
                            columns={columns}
                            expandedRowRender={(record) => {
                                return showCdrSwitchedCall(record);
                            }
                            }
                            rowSelection={rowSelection}
                            expandIcon={(props) => customExpandIcon(props)}
                            dataSource={data}
                            bordered
                            locale={{
                                emptyText: (
                                    <Empty
                                        description={i18next.t("Empty")}
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                ),
                            }}
                            rowKey={(record) => record.cdrId}
                            pagination={pagination}
                            onChange={handleTableChange}
                            loading={isLoading.isFetching}

                        />
                    </div>
                    <Modal
                        title={
                            operationKey == 1
                                ? t(" Confirm Exproting Data")
                                : t(" Confirm Exporting Data With Files")
                        }
                        visible={showModal}
                        onOk={handleOk}
                        onCancel={handleCancel}
                        okText={t("OK")}
                        cancelText={t("Cancel")}
                    >
                        <Form.Item
                            label={t("File Name")}
                            labelCol={{ span: 4 }}
                            wrapperCol={{
                                span: 18,
                            }}
                        >
                            <Input
                                id="filename"
                                onChange={getExportFileName}
                                style={{ marginLeft: 10 }}
                            ></Input>
                        </Form.Item>
                    </Modal>
                </Page>
            )}
        </Translation>
    );
}